/**
 * Allowed keys for accountMembers.menuTabsBlocked (denylist: blocked components).
 * Keep in sync with Cloud Functions v2/functions/src/dashboardUsers/menuTabPermissions.js
 */
export const ALLOWED_MENU_TAB_KEYS = [
  "executive",
  "bookings",
  "bookingFinancials",
  "bookingBudgetAllocation",
  "leads",
  "funnels",
  "pipelineLeadDelete",
  "services",
  "customers",
  "warranties",
  "finance",
  "inventory",
  "employees",
  "accountUsers",
  "shopfront",
  "gallery",
  "referrals",
  "promoter",
  "giftCards",
  "scripts",
  "integrations",
  "feedback",
  "partnerPricing",
  "liveChat",
  "influencers",
  "partnerActivity",
];

const ALLOWED_SET = new Set(ALLOWED_MENU_TAB_KEYS);

/** Legacy + current keys that all deny access to the same nav item when present in menuTabsBlocked. */
export const MENU_TAB_BLOCK_ALIASES = {
  influencers: ["influencers", "influencerOutreach"],
};

/**
 * @param {string} tabName Nav item `name` or permission key
 * @returns {string}
 */
export function resolveMenuTabPermissionKey(tabName) {
  const raw = normalizeTabName(tabName);
  if (!raw) return "";
  const lower = raw.toLowerCase();
  const navAliases = { pipelines: "funnels", influenceroutreach: "influencers" };
  if (navAliases[lower]) return navAliases[lower];
  return ALLOWED_MENU_TAB_KEYS.find((k) => k.toLowerCase() === lower) || raw;
}

/**
 * @param {string} permissionKey
 * @returns {string[]}
 */
export function getBlockedAliasesForPermissionKey(permissionKey) {
  return MENU_TAB_BLOCK_ALIASES[permissionKey] || [permissionKey];
}

/**
 * @param {string} permissionKey
 * @param {unknown} blockedList
 * @returns {boolean}
 */
export function isMenuTabPermissionBlocked(permissionKey, blockedList) {
  const blocked = new Set(normalizeMenuTabsBlocked(blockedList));
  return getBlockedAliasesForPermissionKey(permissionKey).some((k) => blocked.has(k));
}

/** Owner-facing labels for Account Users UI (order matches typical nav grouping). Excludes dev-only nav items (e.g. Smart Cards). */
export const ACCOUNT_COMPONENT_PERMISSIONS = [
  { key: "executive", label: "Executive" },
  { key: "bookings", label: "Bookings" },
  { key: "bookingFinancials", label: "Booking Financial Visibility", parentKey: "bookings" },
  {
    key: "bookingBudgetAllocation",
    label: "Booking Budget Allocation (shadow)",
    parentKey: "bookings",
  },
  { key: "leads", label: "Leads" },
  { key: "funnels", label: "Pipelines" },
  { key: "pipelineLeadDelete", label: "deleting leads from pipelines", parentKey: "funnels" },
  { key: "services", label: "Services" },
  { key: "customers", label: "Customers" },
  { key: "warranties", label: "Warranties" },
  { key: "finance", label: "Finance" },
  { key: "inventory", label: "Inventory" },
  { key: "employees", label: "Technicians" },
  { key: "accountUsers", label: "Account Users" },
  { key: "shopfront", label: "Shopfront" },
  { key: "gallery", label: "Gallery" },
  { key: "referrals", label: "Referrals" },
  { key: "promoter", label: "Promoter (Shadow)" },
  { key: "influencers", label: "Influencers (shadow)" },
  { key: "partnerActivity", label: "Partner Activity (shadow)" },
  { key: "giftCards", label: "Gift Cards" },
  { key: "scripts", label: "Scripts" },
  { key: "integrations", label: "Integrations" },
  { key: "feedback", label: "Feedback" },
  { key: "partnerPricing", label: "Partner Pricing" },
  { key: "liveChat", label: "Live chat" },
];

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
/**
 * Keys the client requested to block but the server did not return (e.g. stale deployed Cloud Function).
 * @param {unknown} requested
 * @param {unknown} persisted
 * @returns {string[]}
 */
export function menuTabsBlockedNotPersisted(requested, persisted) {
  const wanted = normalizeMenuTabsBlocked(requested);
  const saved = new Set(normalizeMenuTabsBlocked(persisted));
  return wanted.filter((key) => !saved.has(key));
}

/**
 * @param {string} key
 * @returns {string}
 */
export function menuTabPermissionLabel(key) {
  const row = ACCOUNT_COMPONENT_PERMISSIONS.find((c) => c.key === key);
  return row?.label || key;
}

export function normalizeMenuTabsBlocked(raw) {
  if (raw == null) return [];

  let items = [];
  if (Array.isArray(raw)) {
    items = raw;
  } else if (typeof raw === "object") {
    // Legacy / mistaken map shape: { bookingFinancials: true }
    items = Object.entries(raw)
      .filter(([, v]) => v === true || v === "true" || v === 1)
      .map(([k]) => k);
  } else {
    return [];
  }

  const out = [];
  const seen = new Set();
  for (const item of items) {
    if (typeof item !== "string") continue;
    let key = item.trim();
    if (key === "influencerOutreach") key = "influencers";
    if (!key || !ALLOWED_SET.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * @param {string} tabName
 * @returns {string}
 */
export function normalizeTabName(tabName) {
  if (typeof tabName !== "string") return "";
  return tabName.trim();
}
